import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { usePersistentState } from "../hooks/usePersistentState";
import { Link, useParams } from "react-router-dom";
import {
  addLayerToSlide,
  alignLayers,
  arrangeSlideText,
  changeLayerZOrder,
  deleteLayerInDeck,
  distributeLayers,
  duplicateLayerInDeck,
  deckSourceContent,
  extractSlideSection,
  insertBlankSlide,
  insertSlideFromSection,
  moveSlideInDeck,
  submitGenerateImageForLayer,
  submitGenerateRasterForTextLayer,
  submitGenerateSlideVisual,
  isSurfJobSubmitResult,
  submitEditSlideImage,
  effectiveThemeForSlide,
  extractBakedTextFromSlide,
  fitLayersToSafeZone,
  getEffectiveCalmZones,
  getTextRenderMode,
  isTextRasterLayer,
  markRasterStale,
  applyGraphicToLayer,
  composeGraphicForSlide,
  normalizeGraphicData,
  runSlideAutofit,
  setImageReferenceKey,
  setRefineInstructions,
  setImageLayerAsset,
  getLayerTextRender,
  getLayerCrop,
  type SurfImageCrop,
  setLayerBakedText,
  setSlideLayers,
  setSlideBackgroundColor,
  syncOutlineFromSlideText,
  toggleLegibilityStrategy,
  touchContentUpdatedAt,
  updateLayerInDeck,
  updateSlideInDeck,
  uploadSurfBlobFile,
  type SurfImageEditTarget,
  type SurfLayer,
  type SurfStyle,
} from "@vida/surf-core";
import { EditorActivityFooter } from "../components/editor/EditorActivityFooter";
import { DeckOverview } from "../components/editor/DeckOverview";
import { EditorCollapsibleBottomPanel } from "../components/editor/EditorCollapsibleBottomPanel";
import { EditorCollapsibleSidebar } from "../components/editor/EditorCollapsibleSidebar";
import { EditorDesignPanel } from "../components/editor/EditorDesignPanel";
import { SlideHistoryPanel } from "../components/editor/SlideHistoryPanel";
import { SlideOutlinePanel } from "../components/editor/SlideOutlinePanel";
import { EditorStylePanel } from "../components/editor/EditorStylePanel";
import { EditorToolbar } from "../components/editor/EditorToolbar";
import { LayerInspector } from "../components/editor/LayerInspector";
import { ImageCropOverlay } from "../components/editor/ImageCropOverlay";
import { SlideCanvas } from "../components/editor/SlideCanvas";
import { SlideThumb } from "../components/editor/SlideThumb";
import { SlideCreateDialog, type SlideCreateChoice } from "../components/editor/SlideCreateDialog";
import { ContentPanel } from "../components/editor/ContentPanel";
import { SpeakerPanel } from "../components/editor/SpeakerPanel";
import { OutlineEditorPanel } from "../components/editor/OutlineEditorPanel";
import { PresentMode } from "../components/present/PresentMode";
import { SurfTopbar } from "../components/shell/SurfTopbar";
import { useDeckEditor } from "../hooks/useDeckEditor";
import { useSurfJobQueue } from "../hooks/useSurfJobQueue";
import { useSurfEditorActivity } from "../hooks/useSurfEditorActivity";
import { useSurfAssetUrls } from "../hooks/useSurfAssetUrls";
import { useToast } from "../ui/useToast";
import { copySpeakerReaderPermalink, speakerReaderPath } from "../utils/speakerReaderLink";
import { saveReturnPath } from "../auth";
import { sampleDominantColor } from "../utils/dominantColor";
import {
  createSurfApiConfig,
  ensureSurfClientConfig,
} from "../services/surfClientConfig";
import { exportSurfDeckToPdf } from "../export/surfPdfExport";
import { exportSurfDeckToPptx } from "../export/surfPptxExport";
import { rasterizeSurfSlide } from "../export/surfSlideRaster";

/** Inline "+" between thumbs (head / between / tail) opening the create dialog at `at`. */
function SlideInsertPoint({
  at,
  label,
  onClick,
}: {
  at: number;
  label: string;
  onClick: () => void;
}) {
  return (
    <div className="slide-insert-point">
      <button
        type="button"
        className="slide-insert-btn"
        aria-label={`Aggiungi slide ${label}`}
        title={`Aggiungi slide ${label}`}
        data-at={at}
        onClick={onClick}
      >
        <span aria-hidden>+</span>
      </button>
    </div>
  );
}

export function DeckEditorPage() {
  const { deckId } = useParams<{ deckId: string }>();
  const {
    row,
    deck,
    slide,
    slideIndex,
    setSlideIndex,
    commit,
    undo,
    redo,
    canUndo,
    canRedo,
    saving,
    dirty,
  } = useDeckEditor(deckId ?? "");

  // Live ref to the current deck so the job queue applies onto the freshest state.
  const deckRef = useRef(deck);
  deckRef.current = deck;

  const [selectedLayerIds, setSelectedLayerIds] = useState<string[]>([]);
  const [showGuides, setShowGuides] = useState(true);
  const [activity, setActivity] = useState<string | null>(null);
  const [imgBusy, setImgBusy] = useState(false);
  const [exporting, setExporting] = useState<"pdf" | "pptx" | null>(null);
  const [focusMode, setFocusMode] = useState(false);
  const [slidesPanelOpen, setSlidesPanelOpen] = usePersistentState("surf.editor.slidesPanelOpen", true);
  const [inspectorPanelOpen, setInspectorPanelOpen] = usePersistentState("surf.editor.inspectorPanelOpen", true);
  const [bottomOpen, setBottomOpen] = usePersistentState("surf.editor.bottomOpen", true);
  const [bottomTab, setBottomTab] = usePersistentState<"designer" | "history" | "outline">(
    "surf.editor.bottomTab",
    "designer",
  );
  const [stylePanelOpen, setStylePanelOpen] = useState(false);
  const [overviewOpen, setOverviewOpen] = useState(false);
  const [presentOpen, setPresentOpen] = useState(false);
  const [cropMode, setCropMode] = useState(false);
  const [editorTab, setEditorTab] = useState<"slides" | "outline" | "content" | "speaker">("slides");
  const [dragSlideIndex, setDragSlideIndex] = useState<number | null>(null);
  const [dropSlideIndex, setDropSlideIndex] = useState<number | null>(null);
  /** Insert index for the slide-creation dialog (null = closed). */
  const [createAt, setCreateAt] = useState<number | null>(null);
  const toast = useToast();
  const { setMessage: setShellActivity, setProgress: setShellProgress } = useSurfEditorActivity();
  const {
    enqueueSubmit,
    hasActiveJobForTarget,
    registerDeckEditor,
    unregisterDeckEditor,
  } = useSurfJobQueue();

  const { backgroundUrls: slideBgUrls, layerImageUrls } = useSurfAssetUrls(deck);

  const selectedLayer = useMemo(() => {
    const id = selectedLayerIds[selectedLayerIds.length - 1];
    return slide?.layers.find((l) => l.id === id) ?? null;
  }, [slide, selectedLayerIds]);

  const singleImageLayerSelected = useMemo(() => {
    if (selectedLayerIds.length !== 1 || !slide) return false;
    const layer = slide.layers.find((l) => l.id === selectedLayerIds[0]);
    return layer?.type === "image";
  }, [slide, selectedLayerIds]);

  const singleGraphicLayerSelected = useMemo(() => {
    if (selectedLayerIds.length !== 1 || !slide) return false;
    const layer = slide.layers.find((l) => l.id === selectedLayerIds[0]);
    return layer?.type === "graphic";
  }, [slide, selectedLayerIds]);

  const singleBakedImageSelected = useMemo(() => {
    if (!singleImageLayerSelected || !selectedLayer) return false;
    return getLayerTextRender(selectedLayer) === "in_image";
  }, [singleImageLayerSelected, selectedLayer]);

  const singleTextLayerSelected = useMemo(() => {
    if (selectedLayerIds.length !== 1 || !slide) return false;
    const layer = slide.layers.find((l) => l.id === selectedLayerIds[0]);
    return layer?.type === "text";
  }, [slide, selectedLayerIds]);

  const runTextRasterGenerate = async () => {
    if (!deck || !slide || !singleTextLayerSelected) return;
    const layerId = selectedLayerIds[0]!;
    const toastId = toast.loading("Illustrazione raster in coda…");
    try {
      await ensureSurfClientConfig();
      const cfg = createSurfApiConfig();
      let workingDeck = deck;
      const bgUrl = slideBgUrls[slide.id];
      if (slide.background.s3Key && !slide.background.dominantColor && bgUrl) {
        try {
          const color = await sampleDominantColor(bgUrl);
          if (color) {
            workingDeck = {
              ...deck,
              slides: deck.slides.map((s) =>
                s.id === slide.id
                  ? { ...s, background: { ...s.background, dominantColor: color } }
                  : s,
              ),
            };
          }
        } catch {
          /* sampling best-effort */
        }
      }
      const { submit, deck: prepared } = await submitGenerateRasterForTextLayer(
        cfg,
        workingDeck,
        slide.id,
        layerId,
        { onWarning: (msg) => toast.info(msg, toastId) },
      );
      await enqueueSubmit(submit, { preparedDeck: prepared });
      toast.success("Illustrazione raster in coda", toastId);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(msg, toastId);
    }
  };

  const runRegenerateAllTextRasters = async () => {
    if (!deck) return;
    const toastId = toast.loading("Illustrazioni testo in coda…");
    try {
      await ensureSurfClientConfig();
      const cfg = createSurfApiConfig();
      let workingDeck = deck;
      const targets: Array<{ slideId: string; layerId: string }> = [];
      for (const s of deck.slides) {
        for (const layer of s.layers) {
          if (layer.type === "text" && getTextRenderMode(layer) === "raster") {
            targets.push({ slideId: s.id, layerId: layer.id });
          }
        }
      }
      for (const { slideId, layerId } of targets) {
        const { submit, deck: prepared } = await submitGenerateRasterForTextLayer(
          cfg,
          workingDeck,
          slideId,
          layerId,
        );
        workingDeck = prepared;
        await enqueueSubmit(submit, { preparedDeck: prepared });
      }
      toast.success(`${targets.length} illustrazioni in coda`, toastId);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(msg, toastId);
    }
  };

  const layerImageSubject = useMemo(() => {
    if (!selectedLayer || selectedLayer.type !== "image") return "";
    const raw = selectedLayer.style?.imageSubject;
    return typeof raw === "string" ? raw : "";
  }, [selectedLayer]);

  useEffect(() => {
    setShellActivity(activity);
  }, [activity, setShellActivity]);

  // Close the crop overlay whenever the selection changes.
  const primarySelectedId = selectedLayerIds[selectedLayerIds.length - 1] ?? null;
  useEffect(() => {
    setCropMode(false);
  }, [primarySelectedId]);

  useEffect(() => {
    setShellProgress(null);
  }, [setShellProgress]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || !e.shiftKey || e.key.toLowerCase() !== "f") return;
      const el = document.activeElement as HTMLElement | null;
      const editing =
        !!el &&
        (el.isContentEditable ||
          el.tagName === "INPUT" ||
          el.tagName === "TEXTAREA" ||
          el.tagName === "SELECT");
      if (editing) return;
      e.preventDefault();
      setFocusMode((prev) => !prev);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const commitDeck = (next: typeof deck) => {
    if (!next) return;
    commit(touchContentUpdatedAt(next));
  };

  useEffect(() => {
    if (!deckId) return;
    registerDeckEditor(
      deckId,
      (next) => {
        commit(touchContentUpdatedAt(next));
      },
      () => deckRef.current,
    );
    return () => unregisterDeckEditor(deckId);
  }, [deckId, commit, registerDeckEditor, unregisterDeckEditor]);

  const selectLayer = (id: string | null, additive?: boolean) => {
    if (!id) {
      setSelectedLayerIds([]);
      return;
    }
    if (additive) {
      setSelectedLayerIds((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
      );
    } else {
      setSelectedLayerIds([id]);
    }
  };

  const runLayerImage = async () => {
    if (!deck || !slide || !singleImageLayerSelected) return;
    const layerId = selectedLayerIds[0]!;
    const subject = layerImageSubject.trim();
    const toastId = toast.loading("Immagine widget in coda…");
    try {
      await ensureSurfClientConfig();
      const cfg = createSurfApiConfig();
      const { submit, deck: prepared } = await submitGenerateImageForLayer(
        cfg,
        deck,
        slide.id,
        layerId,
        subject ? { subject } : undefined,
      );
      await enqueueSubmit(submit, { preparedDeck: prepared });
      toast.success("Immagine in coda", toastId);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(msg, toastId);
    }
  };

  const runImageEdit = async (target: SurfImageEditTarget, instructions: string) => {
    if (!deck || !slide) return;
    const toastId = toast.loading("Modifica immagine in coda…");
    try {
      await ensureSurfClientConfig();
      const cfg = createSurfApiConfig();
      const { submit, deck: prepared } = await submitEditSlideImage(
        cfg,
        deck,
        target,
        instructions,
      );
      await enqueueSubmit(submit, { preparedDeck: prepared });
      toast.success("Modifica in coda", toastId);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(msg, toastId);
    }
  };

  const uploadImageReference = async (target: SurfImageEditTarget, file: File) => {
    if (!deck || !slide) return;
    setImgBusy(true);
    setActivity("Caricamento riferimento…");
    try {
      await ensureSurfClientConfig();
      const cfg = createSurfApiConfig();
      const { s3Key } = await uploadSurfBlobFile(cfg, file);
      commitDeck(setImageReferenceKey(deck, target, s3Key));
      setActivity("Riferimento caricato.");
      setTimeout(() => setActivity(null), 2000);
    } catch (e) {
      setActivity(e instanceof Error ? e.message : String(e));
    } finally {
      setImgBusy(false);
    }
  };

  const uploadImageFromDisk = async (file: File) => {
    if (!deck || !slide || !singleImageLayerSelected) return;
    const layerId = selectedLayerIds[0]!;
    setImgBusy(true);
    setActivity("Caricamento immagine…");
    try {
      await ensureSurfClientConfig();
      const cfg = createSurfApiConfig();
      const { s3Key } = await uploadSurfBlobFile(cfg, file);
      let naturalWidth: number | undefined;
      let naturalHeight: number | undefined;
      try {
        const bitmap = await createImageBitmap(file);
        naturalWidth = bitmap.width;
        naturalHeight = bitmap.height;
        bitmap.close();
      } catch {
        // Fall back to current widget height if dimensions cannot be read.
      }
      commitDeck(
        setImageLayerAsset(deck, slide.id, layerId, s3Key, {
          naturalWidth,
          naturalHeight,
        }),
      );
      setActivity("Immagine caricata.");
      setTimeout(() => setActivity(null), 2000);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setActivity(msg);
      toast.error(msg);
    } finally {
      setImgBusy(false);
    }
  };

  const patchRefineInstructions = (target: SurfImageEditTarget, instructions: string) => {
    if (!deck) return;
    commitDeck(setRefineInstructions(deck, target, instructions));
  };

  const runGraphicCompose = async () => {
    if (!deck || !slide || !singleGraphicLayerSelected) return;
    const layerId = selectedLayerIds[0]!;
    setImgBusy(true);
    setActivity("Composizione illustrazione AI…");
    try {
      await ensureSurfClientConfig();
      const cfg = createSurfApiConfig();
      const graphic = await composeGraphicForSlide(cfg, deck, slide.id, layerId);
      commitDeck(applyGraphicToLayer(deck, slide.id, layerId, normalizeGraphicData(graphic)));
      setActivity(null);
    } catch (e) {
      setActivity(e instanceof Error ? e.message : String(e));
    } finally {
      setImgBusy(false);
    }
  };

  const runVisuals = async (all: boolean) => {
    if (!deck || !slide) return;
    const label = all ? "Immagini contestuali in coda…" : "Immagine contestuale in coda…";
    const toastId = toast.loading(label);
    try {
      await ensureSurfClientConfig();
      const cfg = createSurfApiConfig();
      if (all) {
        let workingDeck = deck;
        for (let index = 0; index < deck.slides.length; index += 1) {
          const submitted = await submitGenerateSlideVisual(cfg, workingDeck, index);
          if (!isSurfJobSubmitResult(submitted)) {
            workingDeck = submitted.deck;
            continue;
          }
          workingDeck = submitted.deck;
          await enqueueSubmit(submitted.submit, { preparedDeck: submitted.deck });
        }
        toast.success("Immagini contestuali in coda", toastId);
      } else {
        const submitted = await submitGenerateSlideVisual(cfg, deck, slideIndex);
        if (!isSurfJobSubmitResult(submitted)) {
          commitDeck(submitted.deck);
          toast.info("Nessuna immagine da generare per questa slide", toastId);
        } else {
          await enqueueSubmit(submitted.submit, { preparedDeck: submitted.deck });
          toast.success("Immagine contestuale in coda", toastId);
        }
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(msg, toastId);
    }
  };

  const autoVisualsKickoffRef = useRef<string | null>(null);
  useEffect(() => {
    if (!deck) return;
    if (autoVisualsKickoffRef.current === deck.id) return;
    const allSlidesSatisfied = deck.slides.every(
      (s) =>
        s.background.s3Key?.trim() ||
        (s.background.solidColor?.trim() && /^#[0-9A-Fa-f]{6}$/i.test(s.background.solidColor.trim())),
    );
    if (allSlidesSatisfied) return;
    const styleReady = Boolean(
      deck.theme.derivedPrompt?.trim() || deck.theme.backgroundTreatment?.trim(),
    );
    if (!styleReady) return;
    autoVisualsKickoffRef.current = deck.id;
    toast.info("Generazione sfondi per slide avviata automaticamente");
    void runVisuals(true);
  }, [deck?.id]);

  const applyEditorStyle = (
    theme: SurfStyle,
    opts: { regenerateBackgrounds: boolean },
  ) => {
    if (!deck) return;
    // Non-destructive: keep slides/outline/text/layout; only swap theme. Clear
    // per-slide background prompt/saliency so a regen uses the NEW style.
    const slides = deck.slides.map((s) => ({
      ...s,
      background: {
        ...s.background,
        prompt: undefined,
        dominantColor: undefined,
        freeZones: undefined,
        busyRegions: undefined,
      },
    }));
    commitDeck({ ...deck, theme, slides });
    setStylePanelOpen(false);
    toast.success("Stile applicato");
    if (opts.regenerateBackgrounds) void runVisuals(true);
  };

  const moveSlide = (from: number, to: number) => {
    if (!deck) return;
    const next = moveSlideInDeck(deck, from, to);
    if (next === deck) return;
    commitDeck(next);
    setSlideIndex(to);
  };

  const insertBlankSlideAt = (at: number) => {
    if (!deck) return;
    const { deck: next, slideId } = insertBlankSlide(deck, at);
    commitDeck(next);
    const newIndex = next.slides.findIndex((s) => s.id === slideId);
    setSlideIndex(newIndex >= 0 ? newIndex : at);
    setSelectedLayerIds([]);
  };

  const runExtractSlideFromContent = async (at: number, topic: string | undefined) => {
    if (!deck) return;
    if (!deckSourceContent(deck).trim()) {
      toast.error("Nessun contenuto sorgente — incollalo nel tab Content.");
      return;
    }

    setImgBusy(true);
    const toastId = toast.loading("Estrazione slide dal contenuto…");
    setActivity("Estrazione slide dal contenuto…");
    try {
      await ensureSurfClientConfig();
      const cfg = createSurfApiConfig();
      const section = await extractSlideSection(cfg, deck, { topic });
      const { deck: next, slideId } = insertSlideFromSection(deck, section, at);
      commitDeck(next);
      const newIndex = next.slides.findIndex((s) => s.id === slideId);
      setSlideIndex(newIndex >= 0 ? newIndex : at);
      setSelectedLayerIds([]);
      toast.success("Slide creata dal contenuto", toastId);
      setActivity(null);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(msg, toastId);
      setActivity(msg);
    } finally {
      setImgBusy(false);
    }
  };

  const handleCreateSlide = async (choice: SlideCreateChoice) => {
    if (createAt === null) return;
    const at = createAt;
    if (choice.mode === "blank") {
      insertBlankSlideAt(at);
      setCreateAt(null);
      return;
    }
    await runExtractSlideFromContent(at, choice.topic);
    setCreateAt(null);
  };

  const applyLayerMutation = (mutate: (layers: SurfLayer[]) => SurfLayer[]) => {
    if (!deck || !slide) return;
    commitDeck(setSlideLayers(deck, slide.id, mutate(slide.layers)));
  };

  const alignSelected = (mode: Parameters<typeof alignLayers>[2]) => {
    if (!selectedLayerIds.length) return;
    applyLayerMutation((layers) => alignLayers(layers, selectedLayerIds, mode));
  };

  const distributeSelected = (axis: "horizontal" | "vertical") => {
    if (!selectedLayerIds.length) return;
    applyLayerMutation((layers) => distributeLayers(layers, selectedLayerIds, axis));
  };

  const fitSelectedToSafe = () => {
    if (!deck || !slide || !selectedLayerIds.length) return;
    const zones = getEffectiveCalmZones(slide, deck.theme);
    const safe = zones[0];
    if (!safe) return;
    applyLayerMutation((layers) => fitLayersToSafeZone(layers, selectedLayerIds, safe));
  };

  const reorganizeSlide = () => {
    if (!deck || !slide) return;
    const theme = effectiveThemeForSlide(deck.theme, slide);
    const arranged = arrangeSlideText(slide, theme);
    commitDeck(setSlideLayers(deck, slide.id, arranged.layers));
  };

  const runAutofitSlide = async () => {
    if (!deck || !slide) return;
    setImgBusy(true);
    const toastId = toast.loading("Adattamento automatico…");
    setActivity("Adattamento automatico…");
    try {
      await ensureSurfClientConfig();
      const cfg = createSurfApiConfig();
      const bgUrl = slideBgUrls[slide.id] ?? null;
      const result = await runSlideAutofit(cfg, deck, slideIndex, (s, theme) =>
        rasterizeSurfSlide({ slide: s, theme, backgroundUrl: bgUrl }),
      );
      if (result.applied) {
        commitDeck(result.deck);
        toast.success(`Layout migliorato (${result.beforeScore}→${result.afterScore} issue)`, toastId);
      } else {
        toast.info("Nessun miglioramento rilevato", toastId);
      }
      setActivity(null);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(msg, toastId);
      setActivity(msg);
    } finally {
      setImgBusy(false);
    }
  };

  const runAutofitAll = async () => {
    if (!deck) return;
    setImgBusy(true);
    const toastId = toast.loading("Adattamento automatico (tutte)…");
    setActivity("Adattamento automatico (tutte)…");
    try {
      await ensureSurfClientConfig();
      const cfg = createSurfApiConfig();
      let working = deck;
      for (let i = 0; i < working.slides.length; i += 1) {
        const s = working.slides[i]!;
        setActivity(`Adatta slide ${i + 1}/${working.slides.length}…`);
        const bgUrl = slideBgUrls[s.id] ?? null;
        const result = await runSlideAutofit(cfg, working, i, (slide, theme) =>
          rasterizeSurfSlide({ slide, theme, backgroundUrl: bgUrl }),
        );
        working = result.deck;
      }
      commitDeck(working);
      toast.success("Adattamento batch completato", toastId);
      setActivity(null);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(msg, toastId);
      setActivity(msg);
    } finally {
      setImgBusy(false);
    }
  };

  const deleteSelected = () => {
    if (!deck || !slide || !selectedLayerIds.length) return;
    let next = deck;
    for (const id of selectedLayerIds) next = deleteLayerInDeck(next, slide.id, id);
    commit(next);
    setSelectedLayerIds([]);
  };

  // Delete / Backspace removes the selected layer(s) — unless typing in a text field.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Delete" && e.key !== "Backspace") return;
      const el = document.activeElement as HTMLElement | null;
      const editing =
        !!el &&
        (el.isContentEditable ||
          el.tagName === "INPUT" ||
          el.tagName === "TEXTAREA" ||
          el.tagName === "SELECT");
      if (editing || !selectedLayerIds.length) return;
      e.preventDefault();
      deleteSelected();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deck, slide, selectedLayerIds]);

  if (!deckId) {
    return (
      <div className="library-page">
        <p className="error">Deck mancante.</p>
        <Link to="/">← Libreria</Link>
      </div>
    );
  }

  if (!row || !deck) {
    return (
      <div className="library-page">
        <p className="muted">Deck non trovato.</p>
        <Link to="/">← Libreria</Link>
      </div>
    );
  }

  if (!deck.slides.length) {
    return (
      <div className="library-page">
        <SurfTopbar title={deck.title} />
        <h1>{deck.title}</h1>
        <p className="muted">Nessuna slide — completa il wizard.</p>
        <Link to={`/deck/${deckId}/wizard`} className="primary-btn">
          Apri wizard
        </Link>
      </div>
    );
  }

  const slideId = slide!.id;
  const busy = saving;
  const targetJobBusy =
    deckId && slide && selectedLayerIds.length === 1
      ? hasActiveJobForTarget(deckId, slide.id, selectedLayerIds[0])
      : false;
  const slideJobBusy = deckId && slide ? hasActiveJobForTarget(deckId, slide.id) : false;
  const saveLabel = saving ? "Salvataggio…" : dirty ? "Modifiche non salvate" : "Salvato";

  const toggleSlidesPanel = () => {
    setSlidesPanelOpen((open) => {
      const next = !open;
      toast.info(next ? "Barra slide espansa" : "Barra slide ridotta");
      return next;
    });
  };

  const toggleInspectorPanel = () => {
    setInspectorPanelOpen((open) => {
      const next = !open;
      toast.info(next ? "Pannello proprietà espanso" : "Pannello proprietà ridotto");
      return next;
    });
  };

  const selectBottomTab = (tab: "designer" | "history" | "outline") => {
    setBottomTab(tab);
    setBottomOpen(true);
  };

  const layoutClassName = [
    "editor-layout",
    "editor-layout-f5",
    focusMode ? "editor-layout--focus" : "",
    !focusMode && !slidesPanelOpen ? "editor-layout--slides-collapsed" : "",
    !focusMode && !inspectorPanelOpen ? "editor-layout--inspector-collapsed" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={`editor-page editor-page--fullscreen${focusMode ? " editor-page--focus" : ""}`}>
      <SurfTopbar
        title={deck.title}
        saveLabel={saveLabel}
        extra={
          <>
            <nav className="editor-view-tabs" aria-label="Vista editor">
              <button
                type="button"
                className={editorTab === "slides" ? "editor-view-tab active" : "editor-view-tab"}
                onClick={() => setEditorTab("slides")}
              >
                Slide
              </button>
              <button
                type="button"
                className={editorTab === "outline" ? "editor-view-tab active" : "editor-view-tab"}
                onClick={() => setEditorTab("outline")}
              >
                Outline
              </button>
              <button
                type="button"
                className={editorTab === "content" ? "editor-view-tab active" : "editor-view-tab"}
                onClick={() => setEditorTab("content")}
              >
                Content
              </button>
              <button
                type="button"
                className={editorTab === "speaker" ? "editor-view-tab active" : "editor-view-tab"}
                onClick={() => setEditorTab("speaker")}
              >
                Speaker
              </button>
            </nav>
            {editorTab === "slides" && (
              <button
                type="button"
                className="surf-btn surf-btn--ghost"
                onClick={() => setPresentOpen(true)}
                disabled={!deck.slides.length}
              >
                Presenta
              </button>
            )}
            <Link
              to={speakerReaderPath(deckId!)}
              className="surf-btn surf-btn--ghost"
              title="Apri lettore speaker fullscreen"
              onClick={() => saveReturnPath(speakerReaderPath(deckId!))}
            >
              Lettore speaker
            </Link>
            <button
              type="button"
              className="surf-btn surf-btn--ghost"
              title="Copia permalink lettore speaker"
              onClick={() =>
                void copySpeakerReaderPermalink(deckId!).then((ok) =>
                  toast.info(ok ? "Permalink copiato" : "Impossibile copiare il link"),
                )
              }
            >
              Copia permalink
            </button>
            <button
              type="button"
              className="surf-btn surf-btn--ghost"
              onClick={() => setFocusMode((v) => !v)}
            >
              {focusMode ? "Esci focus" : "Focus ⌘⇧F"}
            </button>
            <button
              type="button"
              className="surf-btn surf-btn--ghost"
              onClick={() => setStylePanelOpen(true)}
            >
              Stile
            </button>
            <Link to={`/deck/${deckId}/wizard`} className="surf-btn surf-btn--ghost">
              Wizard
            </Link>
          </>
        }
      />

      {stylePanelOpen && (
        <EditorStylePanel
          deck={deck}
          onApply={applyEditorStyle}
          onClose={() => setStylePanelOpen(false)}
        />
      )}

      {createAt !== null && (
        <SlideCreateDialog
          position={createAt}
          total={deck.slides.length}
          hasSourceContent={Boolean(deckSourceContent(deck).trim())}
          busy={imgBusy}
          onCreate={(choice) => void handleCreateSlide(choice)}
          onClose={() => setCreateAt(null)}
        />
      )}

      {editorTab === "outline" ? (
        <div className="editor-outline-view">
          <OutlineEditorPanel deck={deck} onDeckChange={commitDeck} />
        </div>
      ) : editorTab === "content" ? (
        <div className="editor-outline-view">
          <ContentPanel deck={deck} onDeckChange={commitDeck} />
        </div>
      ) : editorTab === "speaker" ? (
        <div className="editor-speaker-view">
          <SpeakerPanel
            deck={deck}
            slideId={slideId}
            slideBackgroundUrl={slideBgUrls[slideId]}
            layerImageUrls={layerImageUrls}
            slideBackgroundUrls={slideBgUrls}
            onSlideIdChange={(id) => {
              const idx = deck.slides.findIndex((s) => s.id === id);
              if (idx >= 0) setSlideIndex(idx);
            }}
            onDeckChange={commitDeck}
            onActivity={setActivity}
          />
        </div>
      ) : (
        <>
      {!focusMode && (
        <EditorToolbar
          busy={busy}
          imgBusy={imgBusy || targetJobBusy || slideJobBusy}
          exporting={exporting}
          canUndo={canUndo}
          canRedo={canRedo}
          showGuides={showGuides}
          selectedCount={selectedLayerIds.length}
          singleImageLayerSelected={singleImageLayerSelected}
          singleGraphicLayerSelected={singleGraphicLayerSelected}
          singleBakedImageSelected={singleBakedImageSelected}
          onUndo={undo}
          onRedo={redo}
          onAddText={() =>
            commit(addLayerToSlide(deck, slideId, "text", { textRole: "body", content: "Testo" }))
          }
          onAddShape={() => commit(addLayerToSlide(deck, slideId, "shape"))}
          onAddImage={() => commit(addLayerToSlide(deck, slideId, "image"))}
          onAddRasterIllustration={() => {
            if (!slide) return;
            const baked = extractBakedTextFromSlide(slide);
            commit(
              addLayerToSlide(deck, slideId, "image", {
                style: { textRender: "in_image", bakedText: baked },
              }),
            );
          }}
          onAddTable={() => commit(addLayerToSlide(deck, slideId, "table"))}
          onAddChart={() => commit(addLayerToSlide(deck, slideId, "chart"))}
          onAddGraphic={() => commit(addLayerToSlide(deck, slideId, "graphic"))}
          onRegenerateGraphic={() => void runGraphicCompose()}
          onGenerateLayerImage={() => void runLayerImage()}
          onGenerateSlideVisual={() => void runVisuals(false)}
          onGenerateAllVisuals={() => void runVisuals(true)}
          onAutofitSlide={() => void runAutofitSlide()}
          onAutofitAll={() => void runAutofitAll()}
          onRegenerateAllTextRasters={() => void runRegenerateAllTextRasters()}
          onReorganize={reorganizeSlide}
          onAlign={alignSelected}
          onDistribute={distributeSelected}
          onFitSafe={fitSelectedToSafe}
          onDelete={deleteSelected}
          onShowOverview={() => setOverviewOpen(true)}
          onToggleGuides={setShowGuides}
          onExportPdf={() => {
            setExporting("pdf");
            const toastId = toast.loading("Export PDF…");
            setActivity("Export PDF…");
            void exportSurfDeckToPdf({
              deck,
              slideBackgroundUrls: slideBgUrls,
              layerImageUrls,
            })
              .then(() => toast.success("PDF esportato", toastId))
              .catch((e: unknown) => {
                const msg = e instanceof Error ? e.message : String(e);
                toast.error(msg, toastId);
                setActivity(msg);
              })
              .finally(() => {
                setExporting(null);
                setTimeout(() => setActivity(null), 2000);
              });
          }}
          onExportPdfWithNotes={() => {
            setExporting("pdf");
            const toastId = toast.loading("Export PDF con note…");
            setActivity("Export PDF con note…");
            void exportSurfDeckToPdf({
              deck,
              slideBackgroundUrls: slideBgUrls,
              layerImageUrls,
              includeSpeakerNotes: true,
            })
              .then(() => toast.success("PDF con note esportato", toastId))
              .catch((e: unknown) => {
                const msg = e instanceof Error ? e.message : String(e);
                toast.error(msg, toastId);
                setActivity(msg);
              })
              .finally(() => {
                setExporting(null);
                setTimeout(() => setActivity(null), 2000);
              });
          }}
          onExportPptx={() => {
            setExporting("pptx");
            const toastId = toast.loading("Export PPTX…");
            setActivity("Export PPTX…");
            void exportSurfDeckToPptx({
              deck,
              slideBackgroundUrls: slideBgUrls,
              layerImageUrls,
            })
              .then(() => toast.success("PPTX esportato", toastId))
              .catch((e: unknown) => {
                const msg = e instanceof Error ? e.message : String(e);
                toast.error(msg, toastId);
                setActivity(msg);
              })
              .finally(() => {
                setExporting(null);
                setTimeout(() => setActivity(null), 2000);
              });
          }}
        />
      )}

      <div className={layoutClassName}>
        {!focusMode && (
          <EditorCollapsibleSidebar
            side="left"
            label="Slide"
            open={slidesPanelOpen}
            onToggle={toggleSlidesPanel}
          >
            <nav className="editor-slide-list" aria-label="Slide">
              <SlideInsertPoint at={0} label="all'inizio" onClick={() => setCreateAt(0)} />
              {deck.slides.map((s, i) => (
                <Fragment key={s.id}>
                  <SlideThumb
                    deck={deck}
                    slide={s}
                    slideIndex={i}
                    slideBackgroundUrl={slideBgUrls[s.id]}
                    layerImageUrls={layerImageUrls}
                    active={i === slideIndex}
                    draggable
                    dragOver={dropSlideIndex === i && dragSlideIndex !== null && dragSlideIndex !== i}
                    canMoveUp={i > 0}
                    canMoveDown={i < deck.slides.length - 1}
                    onMoveUp={() => moveSlide(i, i - 1)}
                    onMoveDown={() => moveSlide(i, i + 1)}
                    onDragStart={(e) => {
                      setDragSlideIndex(i);
                      e.dataTransfer.effectAllowed = "move";
                      e.dataTransfer.setData("text/plain", String(i));
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = "move";
                      setDropSlideIndex(i);
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      const from =
                        dragSlideIndex ?? Number.parseInt(e.dataTransfer.getData("text/plain"), 10);
                      if (Number.isFinite(from) && from !== i) moveSlide(from, i);
                      setDragSlideIndex(null);
                      setDropSlideIndex(null);
                    }}
                    onDragEnd={() => {
                      setDragSlideIndex(null);
                      setDropSlideIndex(null);
                    }}
                    onClick={() => {
                      setSlideIndex(i);
                      setSelectedLayerIds([]);
                    }}
                  />
                  <SlideInsertPoint
                    at={i + 1}
                    label={i + 1 >= deck.slides.length ? "in coda" : `dopo la slide ${i + 1}`}
                    onClick={() => setCreateAt(i + 1)}
                  />
                </Fragment>
              ))}
            </nav>
          </EditorCollapsibleSidebar>
        )}

        <div className="editor-canvas-column">
          <div className="editor-canvas-scroll">
            <SlideCanvas
            deck={deck}
            slide={slide!}
            slideBackgroundUrl={slideBgUrls[slideId] ?? null}
            layerImageUrls={layerImageUrls}
            selectedLayerIds={selectedLayerIds}
            showGuides={showGuides}
            onSelectLayer={selectLayer}
            onLayerRect={(layerId, rect) => commit(updateLayerInDeck(deck, slideId, layerId, rect))}
            onLayerContent={(layerId, content) => {
              const layer = slide!.layers.find((l) => l.id === layerId);
              const patch: Partial<SurfLayer> = { content };
              if (layer?.type === "text" && isTextRasterLayer(layer)) {
                patch.style = markRasterStale(layer, true).style;
              }
              let next = updateLayerInDeck(deck, slideId, layerId, patch);
              if (layer?.type === "text" && layer.textRole) {
                next = syncOutlineFromSlideText(next, slideId, {
                  textRole: layer.textRole,
                  content,
                  layerId,
                });
              }
              commit(next);
            }}
            onLayerGraphic={(layerId, graphic) =>
              commit(
                updateLayerInDeck(deck, slideId, layerId, {
                  style: {
                    ...slide!.layers.find((l) => l.id === layerId)?.style,
                    graphic: normalizeGraphicData(graphic),
                  },
                }),
              )
            }
          />
          </div>
          {!focusMode && (
          <EditorCollapsibleBottomPanel
            label={
              bottomTab === "designer" ? "Designer" : bottomTab === "history" ? "History" : "Outline"
            }
            open={bottomOpen}
            onToggle={() => setBottomOpen((o) => !o)}
            headerExtra={
              <div className="editor-bottom-tabs" role="tablist">
                {(
                  [
                    ["designer", "Designer"],
                    ["history", "History"],
                    ["outline", "Outline"],
                  ] as const
                ).map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    role="tab"
                    aria-selected={bottomTab === id}
                    className={bottomTab === id ? "editor-bottom-tab active" : "editor-bottom-tab"}
                    onClick={() => selectBottomTab(id)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            }
          >
            {bottomTab === "designer" && (
              <EditorDesignPanel
                deck={deck}
                slideIndex={slideIndex}
                slideId={slideId}
                disabled={busy}
                onActivity={setActivity}
                onDeckChange={commitDeck}
              />
            )}
            {bottomTab === "history" && <SlideHistoryPanel deckId={deckId} slide={slide!} />}
            {bottomTab === "outline" && (
              <SlideOutlinePanel deck={deck} slide={slide!} onDeckChange={commitDeck} />
            )}
          </EditorCollapsibleBottomPanel>
          )}
        </div>

        {!focusMode && (
        <EditorCollapsibleSidebar
          side="right"
          label="Proprietà"
          open={inspectorPanelOpen}
          onToggle={toggleInspectorPanel}
        >
        <LayerInspector
          layer={selectedLayer}
          slide={slide}
          slideIndex={slideIndex}
          theme={deck.theme}
          legibilityStrategy={deck.theme.legibility.strategy}
          imageEditBusy={imgBusy || targetJobBusy}
          onSlideBackgroundPromptChange={(prompt) => {
            if (!deck || !slide) return;
            commitDeck(
              updateSlideInDeck(deck, slide.id, (s) => ({
                ...s,
                background: { ...s.background, prompt: prompt || undefined },
              })),
            );
          }}
          onSlideBackgroundColorChange={(hex) => {
            if (!deck || !slide) return;
            commitDeck(setSlideBackgroundColor(deck, slide.id, hex));
          }}
          onImageRefineInstructions={(instructions) => {
            const id = selectedLayerIds[selectedLayerIds.length - 1];
            if (!id) return;
            patchRefineInstructions({ kind: "layer", slideId, layerId: id }, instructions);
          }}
          onEditImageLayer={(instructions) => {
            const id = selectedLayerIds[selectedLayerIds.length - 1];
            if (!id) return;
            patchRefineInstructions({ kind: "layer", slideId, layerId: id }, instructions);
            void runImageEdit({ kind: "layer", slideId, layerId: id }, instructions);
          }}
          onUploadImageReference={(file) => {
            const id = selectedLayerIds[selectedLayerIds.length - 1];
            if (!id) return;
            void uploadImageReference({ kind: "layer", slideId, layerId: id }, file);
          }}
          onUploadImageFromDisk={(file) => void uploadImageFromDisk(file)}
          onOpenImageCrop={() => setCropMode(true)}
          onResetImageCrop={() => {
            const id = selectedLayerIds[selectedLayerIds.length - 1];
            if (!id) return;
            const layer = slide?.layers.find((l) => l.id === id);
            if (!layer) return;
            const { crop: _drop, ...restStyle } = layer.style ?? {};
            void _drop;
            commit(updateLayerInDeck(deck, slideId, id, { style: restStyle }));
          }}
          onBackgroundRefineInstructions={(instructions) =>
            patchRefineInstructions({ kind: "background", slideId }, instructions)
          }
          onEditBackground={(instructions) => {
            patchRefineInstructions({ kind: "background", slideId }, instructions);
            void runImageEdit({ kind: "background", slideId }, instructions);
          }}
          onUploadBackgroundReference={(file) =>
            void uploadImageReference({ kind: "background", slideId }, file)
          }
          onSeedBakedTextFromSlide={() => {
            const id = selectedLayerIds[selectedLayerIds.length - 1];
            if (!id || !slide) return;
            const layer = slide.layers.find((l) => l.id === id);
            if (!layer || layer.type !== "image") return;
            const baked = extractBakedTextFromSlide(slide);
            commit(
              updateLayerInDeck(deck, slideId, id, setLayerBakedText(layer, baked)),
            );
          }}
          onGenerateTextRaster={() => void runTextRasterGenerate()}
          onTextRasterRefineInstructions={(instructions) => {
            const id = selectedLayerIds[selectedLayerIds.length - 1];
            if (!id) return;
            patchRefineInstructions({ kind: "textRaster", slideId, layerId: id }, instructions);
          }}
          onEditTextRaster={(instructions) => {
            const id = selectedLayerIds[selectedLayerIds.length - 1];
            if (!id) return;
            patchRefineInstructions({ kind: "textRaster", slideId, layerId: id }, instructions);
            void runImageEdit({ kind: "textRaster", slideId, layerId: id }, instructions);
          }}
          onUploadTextRasterReference={(file) => {
            const id = selectedLayerIds[selectedLayerIds.length - 1];
            if (!id) return;
            void uploadImageReference({ kind: "textRaster", slideId, layerId: id }, file);
          }}
          onPatch={(patch) => {
            const id = selectedLayerIds[selectedLayerIds.length - 1];
            if (!id) return;
            const layer = slide?.layers.find((l) => l.id === id);
            let finalPatch = patch;
            if (
              layer?.type === "text" &&
              isTextRasterLayer(layer) &&
              patch.content !== undefined &&
              patch.content !== layer.content
            ) {
              finalPatch = {
                ...patch,
                style: markRasterStale(layer, true).style,
              };
            }
            commit(updateLayerInDeck(deck, slideId, id, finalPatch));
          }}
          onImageSubjectChange={(subject) => {
            const id = selectedLayerIds[selectedLayerIds.length - 1];
            if (!id) return;
            const layer = slide?.layers.find((l) => l.id === id);
            if (!layer) return;
            commit(
              updateLayerInDeck(deck, slideId, id, {
                style: { ...layer.style, imageSubject: subject },
              }),
            );
          }}
          onZOrder={(mode) => {
            const id = selectedLayerIds[selectedLayerIds.length - 1];
            if (!id) return;
            commit(changeLayerZOrder(deck, slideId, id, mode));
          }}
          onDelete={() => {
            const id = selectedLayerIds[selectedLayerIds.length - 1];
            if (!id) return;
            commit(deleteLayerInDeck(deck, slideId, id));
            setSelectedLayerIds((prev) => prev.filter((x) => x !== id));
          }}
          onDuplicate={() => {
            const id = selectedLayerIds[selectedLayerIds.length - 1];
            if (!id) return;
            const next = duplicateLayerInDeck(deck, slideId, id);
            commit(next);
            const newLayer = next.slides[slideIndex]?.layers.at(-1);
            if (newLayer) setSelectedLayerIds([newLayer.id]);
          }}
          onLegibility={(strategy) => commit(toggleLegibilityStrategy(deck, strategy))}
        />
        </EditorCollapsibleSidebar>
        )}
      </div>

      <EditorActivityFooter activity={activity} progress={null} />

      {overviewOpen && (
        <DeckOverview
          deck={deck}
          slideIndex={slideIndex}
          slideBackgroundUrls={slideBgUrls}
          layerImageUrls={layerImageUrls}
          onSelectSlide={setSlideIndex}
          onClose={() => setOverviewOpen(false)}
          onMoveSlide={moveSlide}
        />
      )}

      {cropMode &&
        selectedLayerIds.length === 1 &&
        selectedLayer &&
        layerImageUrls[selectedLayer.id] &&
        (selectedLayer.type === "image" ||
          (selectedLayer.type === "text" && isTextRasterLayer(selectedLayer))) && (
          <ImageCropOverlay
            imageUrl={layerImageUrls[selectedLayer.id]!}
            crop={getLayerCrop(selectedLayer)}
            onConfirm={(crop: SurfImageCrop) => {
              commit(
                updateLayerInDeck(deck, slideId, selectedLayer.id, {
                  style: { ...selectedLayer.style, crop },
                }),
              );
              setCropMode(false);
            }}
            onReset={() => {
              const { crop: _drop, ...restStyle } = selectedLayer.style ?? {};
              void _drop;
              commit(updateLayerInDeck(deck, slideId, selectedLayer.id, { style: restStyle }));
              setCropMode(false);
            }}
            onCancel={() => setCropMode(false)}
          />
        )}

      {presentOpen && (
        <PresentMode
          deck={deck}
          initialIndex={slideIndex}
          slideBgUrls={slideBgUrls}
          layerImageUrls={layerImageUrls}
          onClose={(finalIndex) => {
            setPresentOpen(false);
            setSlideIndex(finalIndex);
            setSelectedLayerIds([]);
          }}
        />
      )}
        </>
      )}
    </div>
  );
}
